import { useEffect, useState } from "react";
import { Plus, Trash2, AlertTriangle, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import CostCodeCombobox from "./CostCodeCombobox";

const money = (v) => {
  const n = Number(v || 0);
  const body = `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2,
    maximumFractionDigits: 2 })}`;
  return n < 0 ? `-${body}` : body;
};

function Field({ label, children }) {
  return (
    <label className="flex items-center justify-between gap-2 py-1">
      <span className="text-[11px] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function NumberInput({ value, onCommit, suffix, testid, step = "0.01" }) {
  const [draft, setDraft] = useState(String(value ?? 0));
  useEffect(() => { setDraft(String(value ?? 0)); }, [value]);
  const commit = () => {
    const parsed = parseFloat(draft);
    if (Number.isNaN(parsed) || parsed === Number(value)) { setDraft(String(value ?? 0)); return; }
    onCommit(parsed);
  };
  return (
    <span className="flex items-center gap-1">
      <Input value={draft} step={step} type="number"
             onChange={(e) => setDraft(e.target.value)} onBlur={commit}
             onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
             className="h-7 w-24 text-right text-xs" data-testid={testid} />
      {suffix && <span className="w-3 text-[11px] text-slate-400">{suffix}</span>}
    </span>
  );
}

function Row({ label, value, strong, testid, negative }) {
  return (
    <div className={`flex items-center justify-between py-1 ${strong ? "border-t border-slate-200 pt-2" : ""}`}>
      <span className={`text-[11px] ${strong ? "font-semibold text-slate-700" : "text-slate-500"}`}>
        {label}
      </span>
      <span className={`tabular-nums ${strong ? "text-sm font-bold text-slate-900" : "text-xs text-slate-700"}`}
            data-testid={testid}>
        {negative && Number(value) ? `(${money(Math.abs(value))})` : money(value)}
      </span>
    </div>
  );
}

export default function SummaryPanel({
  defaults, rollup, misc, costCodes, settingsTexturaRate, onSaveDefaults, onApplyGm,
  onAddMisc, onUpdateMisc, onDeleteMisc, readOnly = false,
}) {
  const [newMisc, setNewMisc] = useState({ label: "", amount: "", type: "add", cost_code_id: "" });
  const d = defaults || {};
  const r = rollup || {};

  const addMisc = () => {
    if (!newMisc.label.trim()) return;
    onAddMisc({
      label: newMisc.label.trim(), amount: parseFloat(newMisc.amount) || 0,
      type: newMisc.type, cost_code_id: newMisc.cost_code_id || null, show_on_proposal: true,
    });
    setNewMisc({ label: "", amount: "", type: "add", cost_code_id: "" });
  };

  return (
    <div className="w-[330px] shrink-0 space-y-3 pb-24" data-testid="estimate-summary-panel">
      <section className={`rounded-lg border border-slate-200 bg-white p-3 ${
        readOnly ? "pointer-events-none opacity-60" : ""}`}>
        <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Global controls
        </h3>
        <Field label="Labor rate ($/hr, burdened)">
          <NumberInput value={d.labor_rate} testid="summary-labor-rate"
                       onCommit={(v) => onSaveDefaults({ labor_rate: v })} />
        </Field>
        <Field label="Labor multiplier">
          <NumberInput value={d.labor_multiplier} testid="summary-labor-multiplier"
                       onCommit={(v) => onSaveDefaults({ labor_multiplier: v })} />
        </Field>
        <Field label="Default material GM">
          <NumberInput value={Number(((d.default_material_gm || 0) * 100).toFixed(2))} suffix="%"
                       testid="summary-material-gm"
                       onCommit={(v) => onSaveDefaults({ default_material_gm: v / 100 })} />
        </Field>
        <Field label="Default labor GM">
          <NumberInput value={Number(((d.default_labor_gm || 0) * 100).toFixed(2))} suffix="%"
                       testid="summary-labor-gm"
                       onCommit={(v) => onSaveDefaults({ default_labor_gm: v / 100 })} />
        </Field>
        <Button variant="outline" size="sm" className="mt-1 w-full text-xs"
                onClick={onApplyGm} data-testid="summary-apply-gm"
                title="Clears every line-level GM override so the whole estimate follows these defaults.">
          <Wand2 className="mr-1.5 h-3.5 w-3.5" /> Apply defaults to all lines
        </Button>
      </section>

      <section className={`rounded-lg border border-slate-200 bg-white p-3 ${
        readOnly ? "pointer-events-none opacity-60" : ""}`}>
        <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Tax, Textura &amp; discount
        </h3>
        <Field label="Tax">
          <NumberInput value={Number(((d.tax_rate || 0) * 100).toFixed(3))} suffix="%"
                       testid="summary-tax-rate"
                       onCommit={(v) => onSaveDefaults({ tax_rate: v / 100 })} />
        </Field>
        <Field label="Textura fee">
          <Switch checked={!!d.textura_enabled} data-testid="summary-textura-enabled"
                  onCheckedChange={(v) => onSaveDefaults(v && !d.textura_rate
                    ? { textura_enabled: true, textura_rate: settingsTexturaRate || 0.0225 }
                    : { textura_enabled: v })} />
        </Field>
        {d.textura_enabled && (
          <Field label="Textura rate">
            <NumberInput value={Number(((d.textura_rate || 0) * 100).toFixed(3))} suffix="%"
                         testid="summary-textura-rate"
                         onCommit={(v) => onSaveDefaults({ textura_rate: v / 100 })} />
          </Field>
        )}
        <Field label="Discount">
          <span className="flex items-center gap-1">
            <select value={d.discount_type || "amount"}
                    onChange={(e) => onSaveDefaults({ discount_type: e.target.value })}
                    className="h-7 rounded border border-slate-200 bg-white px-1 text-xs"
                    data-testid="summary-discount-type">
              <option value="amount">$</option>
              <option value="percent">%</option>
            </select>
            <NumberInput
              value={d.discount_type === "percent"
                ? Number(((d.discount_value || 0) * 100).toFixed(2)) : d.discount_value}
              testid="summary-discount-value"
              onCommit={(v) => onSaveDefaults({
                discount_value: d.discount_type === "percent" ? v / 100 : v,
              })} />
          </span>
        </Field>
      </section>

      <section className={`rounded-lg border border-slate-200 bg-white p-3 ${
        readOnly ? "pointer-events-none opacity-60" : ""}`}>
        <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Misc lines
        </h3>
        <div className="space-y-1" data-testid="summary-misc-list">
          {(misc || []).length === 0 && (
            <p className="py-1 text-[11px] text-slate-400">No misc adds or deducts.</p>
          )}
          {(misc || []).map((m) => (
            <div key={m.id} className="flex items-center gap-1.5 rounded bg-slate-50 px-2 py-1"
                 data-testid={`misc-row-${m.id}`}>
              <span className="flex-1 truncate text-[11px] text-slate-700" title={m.label}>
                {m.label}
              </span>
              <span className={`text-[11px] tabular-nums ${
                m.type === "deduct" ? "text-red-600" : "text-slate-700"}`}>
                {m.type === "deduct" ? "−" : ""}{money(m.amount)}
              </span>
              <button title={m.show_on_proposal ? "Shown on the proposal — click to make internal"
                             : "Internal only — click to show on the proposal"}
                      onClick={() => onUpdateMisc(m.id, { ...m, show_on_proposal: !m.show_on_proposal })}
                      className={`rounded px-1 text-[10px] font-semibold ${
                        m.show_on_proposal ? "bg-[#007BFF]/10 text-[#007BFF]" : "bg-slate-200 text-slate-400"}`}
                      data-testid={`misc-show-${m.id}`}>
                {m.show_on_proposal ? "P" : "int"}
              </button>
              <button onClick={() => onDeleteMisc(m.id)} className="text-slate-400 hover:text-red-600"
                      data-testid={`misc-delete-${m.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-2 space-y-1.5">
          <Input placeholder="Label (e.g. Freight)" value={newMisc.label}
                 onChange={(e) => setNewMisc({ ...newMisc, label: e.target.value })}
                 className="h-7 text-xs" data-testid="misc-label-input" />
          <div className="flex gap-1.5">
            <select value={newMisc.type}
                    onChange={(e) => setNewMisc({ ...newMisc, type: e.target.value })}
                    className="h-7 rounded border border-slate-200 bg-white px-1 text-xs"
                    data-testid="misc-type-select">
              <option value="add">Add</option>
              <option value="deduct">Deduct</option>
            </select>
            <Input placeholder="0.00" type="number" value={newMisc.amount}
                   onChange={(e) => setNewMisc({ ...newMisc, amount: e.target.value })}
                   className="h-7 flex-1 text-right text-xs" data-testid="misc-amount-input" />
          </div>
          <CostCodeCombobox value={newMisc.cost_code_id} costCodes={costCodes}
                            onChange={(v) => setNewMisc({ ...newMisc, cost_code_id: v })}
                            testid="misc-cost-code-select" />
          <Button size="sm" className="w-full text-xs" onClick={addMisc} data-testid="misc-add-btn">
            <Plus className="mr-1 h-3.5 w-3.5" /> Misc
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3">
        <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Totals
        </h3>
        <Row label="Material" value={r.total_material} testid="total-material" />
        <div className="flex items-center justify-between py-1">
          <span className="text-[11px] text-slate-500">Labor hours</span>
          <span className="text-xs tabular-nums text-slate-700" data-testid="total-labor-hours">
            {Number(r.total_labor_hours || 0).toFixed(2)}
          </span>
        </div>
        <Row label="Labor" value={r.total_labor} testid="total-labor" />
        <Row label="Misc" value={r.total_misc} testid="total-misc" />
        <Row label="Discount" value={r.total_discount} negative testid="total-discount" />
        <Row label="Subtotal" value={r.subtotal} testid="total-subtotal" />
        <Row label="Sales tax" value={r.total_tax} testid="total-tax" />
        <Row label="Textura" value={r.total_textura} testid="total-textura" />
        <Row label="TOTAL SALES" value={r.total_sales} strong testid="total-sales" />
        <div className="mt-1 flex items-center justify-between">
          <span className="text-[11px] text-slate-500">Gross margin</span>
          <span className="text-xs font-semibold tabular-nums text-emerald-700" data-testid="total-gm">
            {money(r.total_gm_dollars)} · {((r.total_gm_percent || 0) * 100).toFixed(1)}%
          </span>
        </div>
        {!!r.total_options && (
          <Row label="Alternates (not in total)" value={r.total_options} testid="total-options" />
        )}
        {!!r.lines_awaiting_quote && (
          <p className="mt-2 flex items-center gap-1 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-700"
             data-testid="awaiting-quote-warning">
            <AlertTriangle className="h-3 w-3" /> {r.lines_awaiting_quote} line(s) awaiting a vendor quote
          </p>
        )}
      </section>

    </div>
  );
}
